/**
 * Image highlighting operations (full image and partial/point)
 */

import { getUniqueId } from '@/lib/commonHelpers';
import { useRealtimeExperienceStore } from '@/pages/experiences/store/realtimeExperienceStore';
import {
  calculateTooltipPosition,
  createActionButtons,
  createMinimizedNote,
  createNoteEditor,
  safeRemoveMinimizedNote,
} from './uiHelpers';

const TOOLTIP_CONFIG = {
  PARTIAL: { width: 230, height: 200, xOffset: 0, yOffset: 88, fallbackY: 20 },
  FULL: { width: 230, height: 200, xOffset: -120, yOffset: 98, fallbackY: 20 },
};

export const FULL_OVERLAY_BORDER_PX = 4;
export const FULL_BADGE_PX = 40;

/**
 * Corner badge offset for a whole-image highlight, inside the image on both axes.
 * Absolutely-positioned children are placed against the overlay's PADDING box, so
 * offset 0 sits just inside the highlight border. The button cluster's
 * `right: 38px` in textHighlighter.css is derived from FULL_BADGE_PX.
 */
export const fullBadgeOffset = (overlayWidth: number) => ({
  left: Math.max(0, overlayWidth - 2 * FULL_OVERLAY_BORDER_PX - FULL_BADGE_PX),
  top: 0,
});

/**
 * Safely remove a DOM element with proper checks
 */
const safeRemoveElement = (element: Element | null | undefined) => {
  if (!element) return;
  try {
    if (document.body.contains(element)) {
      element.remove();
    }
  } catch (error) {
    console.warn('Error removing element:', error);
  }
};

/**
 * Apply saved image highlights to DOM
 */
export const applyImageHighlights = (
  imageHighlights: any[],
  highlightsIdentifier: string,
  htmlRef: HTMLElement,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean
) => {
  const currentHighlights = imageHighlights;

  console.log('🎨 applyImageHighlights', {
    totalHighlights: imageHighlights.length,
    currentHighlights: currentHighlights.length,
    highlightsIdentifier,
  });

  // Remove overlays that are no longer in the highlights list
  const existingOverlays = htmlRef.querySelectorAll('.image-highlight-overlay');
  existingOverlays.forEach((overlay) => {
    const element = overlay as HTMLElement;
    const id = element.dataset.highlightId;
    // Remove if NOT in current highlights (i.e., it was deleted)
    if (id && !currentHighlights.some((h: any) => h.id === id)) {
      console.log('🗑️ Removing orphaned overlay:', id);

      // If it's a full highlight, remove the marker from the image
      if (element.dataset.highlightType === 'full') {
        const image = element.parentElement?.querySelector('img');
        if (image) {
          delete (image as HTMLImageElement).dataset.hasFullHighlight;
        }
      }

      safeRemoveElement(element);
    }
  });

  // Apply current highlights (skip if already exists to avoid duplicates)
  currentHighlights.forEach((highlight: any) => {
    // Check if overlay already exists
    const existingOverlay = htmlRef.querySelector(
      `.image-highlight-overlay[data-highlight-id="${highlight.id}"]`
    );

    if (existingOverlay) {
      console.log('⏭️ Overlay already exists, skipping:', highlight.id);
      return; // Skip if already rendered
    }

    const images = htmlRef.querySelectorAll('img');
    const targetImage = Array.from(images).find((img: any) => img.src === highlight.image_src);
    if (targetImage) {
      console.log('✅ Creating overlay for:', highlight.id);

      const overlay = createImageOverlay(
        targetImage as HTMLImageElement,
        highlight,
        onUpdate,
        translate,
        commentsVisible
      );

      // Mark the image if overlay creation succeeded and it's a full highlight
      if (overlay && highlight.highlight_type === 'full') {
        (targetImage as HTMLImageElement).dataset.hasFullHighlight = 'true';
      }
    }
  });
};

/**
 * Handle image click - show highlight options
 */
export const handleImageClick = (
  e: MouseEvent,
  highlightsIdentifier: string,
  imageHighlights: any[],
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean
) => {
  // Skip image highlighting if highlightsIdentifier doesn't exist
  if (!highlightsIdentifier) {
    console.log('⏭️ Skipping image highlight - no highlightsIdentifier provided');
    return;
  }

  const target = e.target as HTMLElement;

  // Check if within editable element
  let node: any = target;
  while (node && node !== document.body) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      if (
        el.classList?.contains('ck-editor') ||
        el.classList?.contains('ck-content') ||
        el.getAttribute?.('contenteditable') === 'true' ||
        el.classList?.contains('draggable-notepad')
      ) {
        return;
      }
    }
    node = node.parentNode;
  }

  // Get image element
  let image: HTMLImageElement | null = null;
  if (target.tagName === 'IMG') {
    image = target as HTMLImageElement;
  } else if (target.classList.contains('image-highlight-overlay')) {
    image = target.parentElement?.querySelector('img') || null;
  }

  if (image) {
    e.preventDefault();
    e.stopPropagation();
    showHighlightOptions(
      image,
      highlightsIdentifier,
      e.clientX,
      e.clientY,
      imageHighlights,
      onUpdate,
      translate,
      commentsVisible
    );
  }
};

/**
 * Show image highlight options (full or partial)
 */
const showHighlightOptions = (
  image: HTMLImageElement,
  highlightsIdentifier: string,
  clickX: number,
  clickY: number,
  imageHighlights: any[],
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean
) => {
  // Remove existing tooltips
  document
    .querySelectorAll('.image-highlight-options-tooltip')
    .forEach((t) => safeRemoveElement(t));

  const imageRect = image.getBoundingClientRect();
  const relativeX = clickX - imageRect.left;
  const relativeY = clickY - imageRect.top;

  // Check if full highlight exists (both in store and in DOM - temporary or permanent)
  const hasFullHighlight =
    // Check in stored highlights
    imageHighlights.some(
      (h) =>
        h.highlight_identifier === highlightsIdentifier &&
        h.image_src === image.src &&
        h.highlight_type === 'full'
    ) ||
    // Also check if this specific image has a full highlight marker
    image.dataset.hasFullHighlight === 'true';

  // Smart positioning
  const tooltipWidth = 250;
  const tooltipHeight = 120;
  let x = clickX + window.scrollX;
  let y = clickY + window.scrollY;

  if (clickX + tooltipWidth > window.innerWidth) {
    x = clickX - tooltipWidth + window.scrollX;
  }
  if (clickY + tooltipHeight > window.innerHeight) {
    y = clickY - tooltipHeight + window.scrollY;
  }

  // Create options dialog
  const dialog = document.createElement('div');
  dialog.className = 'image-highlight-options-tooltip';
  dialog.style.cssText = `
    position: absolute; left: ${x}px; top: ${y}px; z-index: 2000;
    background: white; border: 1px solid #e5e7eb; border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15); width: 230px;
  `;

  const options: string[] = [];
  if (!hasFullHighlight) {
    options.push(`
      <button class="option-btn" data-action="full">
        ${translate('Highlighter.add_note_whole_image')}
      </button>
    `);
  }
  options.push(`
    <button class="option-btn" data-action="partial">
      ${translate('Highlighter.add_note_point_on_image')}
    </button>
  `);

  dialog.innerHTML = options.join('');
  document.body.appendChild(dialog);

  // Add styles for buttons
  dialog.querySelectorAll('.option-btn').forEach((btn) => {
    (btn as HTMLElement).style.cssText = `
      width: 100%; padding: 10px 13px; background: white; border: none;
      border-bottom: 1px solid #f3f4f6; font-size: 14px; color: #6b7280;
      text-align: left; cursor: pointer; transition: background-color 0.15s;
    `;
    btn.addEventListener('mouseenter', () => {
      (btn as HTMLElement).style.backgroundColor = '#f9fafb';
    });
    btn.addEventListener('mouseleave', () => {
      (btn as HTMLElement).style.backgroundColor = 'white';
    });
  });

  // Handle option selection
  dialog.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const btn = target.closest('.option-btn') as HTMLElement;
    if (!btn) return;

    const action = btn.dataset.action;
    if (action === 'full') {
      createFullImageHighlight(image, highlightsIdentifier, onUpdate, translate, commentsVisible);
    } else if (action === 'partial') {
      createPartialImageHighlight(
        image,
        highlightsIdentifier,
        relativeX,
        relativeY,
        onUpdate,
        translate,
        commentsVisible
      );
    }
    safeRemoveElement(dialog);
  });

  // Close on click outside
  const handleClickOutside = (e: MouseEvent) => {
    if (!dialog.contains(e.target as Node)) {
      safeRemoveElement(dialog);
      document.removeEventListener('click', handleClickOutside);
    }
  };
  setTimeout(() => document.addEventListener('click', handleClickOutside), 50);
};

/**
 * Create full image highlight (border around entire image)
 */
const createFullImageHighlight = (
  image: HTMLImageElement,
  highlightsIdentifier: string,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean
) => {
  // const color = useExperienceStore.getState().selectedHighlightColor || '#7dffff';
  const color = useRealtimeExperienceStore.getState().selectedHighlightColor || '#7dffff';
  const highlight = {
    id: getUniqueId(),
    highlight_identifier: highlightsIdentifier,
    image_src: image.src,
    image_alt: image.alt || '',
    highlight_type: 'full',
    color,
    note: '',
    position: { x: 0, y: 0, width: 100, height: 100 },
  };

  // Create as temporary - will save only when note is added
  const overlay = createImageOverlay(image, highlight, onUpdate, translate, commentsVisible, true);

  // Mark the image if overlay creation succeeded
  if (overlay) {
    image.dataset.hasFullHighlight = 'true';
  }

  // Always open note editor for new highlights (even if commentsVisible is false)
  // If no note is added, the highlight will be removed in onClose
  if (overlay) {
    setTimeout(() => openImageNoteEditor(overlay, highlight, onUpdate, translate), 0);
  }
};

/**
 * Create partial image highlight (point on image)
 */
const createPartialImageHighlight = (
  image: HTMLImageElement,
  highlightsIdentifier: string,
  clickX: number,
  clickY: number,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean
) => {
  // const color = useExperienceStore.getState().selectedHighlightColor || '#7dffff';
  const color = useRealtimeExperienceStore.getState().selectedHighlightColor || '#7dffff';
  const imageRect = image.getBoundingClientRect();
  const highlight = {
    id: getUniqueId(),
    highlight_identifier: highlightsIdentifier,
    image_src: image.src,
    image_alt: image.alt || '',
    highlight_type: 'partial',
    color,
    note: '',
    position: {
      x: ((clickX - 16) / imageRect.width) * 100,
      y: ((clickY - 16) / imageRect.height) * 100,
      width: (32 / imageRect.width) * 100,
      height: (32 / imageRect.height) * 100,
    },
  };

  // Create as temporary - will save only when note is added
  const overlay = createImageOverlay(image, highlight, onUpdate, translate, commentsVisible, true);

  // Always open note editor for new highlights (even if commentsVisible is false)
  // If no note is added, the highlight will be removed in onClose
  if (overlay) {
    setTimeout(() => openImageNoteEditor(overlay, highlight, onUpdate, translate), 0);
  }
};

/**
 * Create image highlight overlay in DOM
 */
const createImageOverlay = (
  image: HTMLImageElement,
  highlight: any,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean,
  isTemporary = false
): HTMLElement | null => {
  const container = image.parentElement;
  if (!container) return null;

  // Check for existing overlay
  const existing = container.querySelector(
    `.image-highlight-overlay[data-highlight-id="${highlight.id}"]`
  );
  if (existing) return null;

  // APL-11850 §27: trap the overlays' inline z-indexes (50/60/95/2000) in the
  // image's own stacking context so they never paint over the sticky header
  // (z-30). Unconditional — already-positioned containers leak z too (W3).
  container.style.isolation = 'isolate';

  // Ensure container positioning
  if (getComputedStyle(container).position === 'static') {
    container.style.position = 'relative';
    if (getComputedStyle(container).display === 'inline') {
      container.style.display = 'inline-block';
    }
  }

  // Create overlay
  const overlay = document.createElement('div');
  overlay.className = 'image-highlight-overlay';
  overlay.dataset.highlightId = highlight.id;
  overlay.dataset.hasNote = highlight.note?.trim() ? 'true' : 'false';
  overlay.dataset.highlightType = highlight.highlight_type || 'partial'; // Add type for CSS targeting
  if (isTemporary) overlay.dataset.temporary = 'true';

  const isFullHighlight = highlight.highlight_type === 'full';

  // Function to update overlay position based on image size
  const updateOverlayPosition = () => {
    const imageRect = image.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate position relative to container
    const offsetX = imageRect.left - containerRect.left;
    const offsetY = imageRect.top - containerRect.top;

    const x = offsetX + (highlight.position.x / 100) * imageRect.width;
    const y = offsetY + (highlight.position.y / 100) * imageRect.height;
    const width = (highlight.position.width / 100) * imageRect.width;
    const height = (highlight.position.height / 100) * imageRect.height;

    if (isFullHighlight) {
      // Full image - border only
      // Always use pointer-events: none for the main overlay to allow zoom to work
      overlay.style.cssText = `
        position: absolute; left: ${x}px; top: ${y}px;
        width: ${width}px; height: ${height}px;
        background: transparent; border: ${FULL_OVERLAY_BORDER_PX}px solid ${highlight.color};
        pointer-events: none;
        cursor: default; z-index: 50; box-sizing: border-box;
      `;

      // Update pointer position
      const pointer = overlay.querySelector('.full-image-corner-pointer') as HTMLElement;
      if (pointer) {
        const badge = fullBadgeOffset(width);
        pointer.style.left = `${badge.left}px`;
        pointer.style.top = `${badge.top}px`;
      }
    } else {
      // Partial - circular point
      overlay.style.cssText = `
        position: absolute; left: ${x}px; top: ${y}px;
        width: 32px; height: 32px; border-radius: 50%;
        background-color: ${highlight.color}; border: 3px solid white;
        box-shadow: rgba(0,0,0,0.3) 0px 2px 8px; display: flex;
        align-items: center; justify-content: center; cursor: pointer;
        transition: transform 0.2s; transform: scale(1.2);
        pointer-events: auto; z-index: 95; box-sizing: border-box;
      `;
    }
  };

  // Initial positioning
  updateOverlayPosition();

  if (isFullHighlight) {
    // Add corner pointer (top-right)
    const pointer = document.createElement('div');
    pointer.className = 'full-image-corner-pointer';
    pointer.style.cssText = `
      position: absolute;
      width: ${FULL_BADGE_PX}px; height: ${FULL_BADGE_PX}px; border-radius: 50%;
      background-color: ${highlight.color}; border: 3px solid white;
      box-shadow: rgba(0,0,0,0.3) 0px 2px 8px; display: flex;
      align-items: center; justify-content: center; cursor: pointer;
      transition: transform 0.2s; pointer-events: auto; z-index: 60;
    `;
    pointer.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1f2937" stroke-width="2"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>`;
    pointer.addEventListener('mouseenter', () => (pointer.style.transform = 'scale(1.1)'));
    pointer.addEventListener('mouseleave', () => (pointer.style.transform = 'scale(1.0)'));
    // pointer.addEventListener('click', (e) => {
    //   e.preventDefault();
    //   e.stopPropagation();
    //   if (useRealtimeExperienceStore.getState().commentsVisible) {
    //     openImageNoteEditor(overlay, highlight, onUpdate, translate);
    //   }
    // });
    overlay.appendChild(pointer);
  } else {
    // Partial - circular point SVG
    overlay.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1f2937" stroke-width="2"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>`;
  }

  container.appendChild(overlay);

  // Add UI components
  updateImageNoteUI(overlay, highlight, onUpdate, translate, commentsVisible);

  // Handle resize/zoom for dynamic repositioning
  const resizeObserver = new ResizeObserver(() => {
    updateOverlayPosition();
  });

  resizeObserver.observe(image);
  resizeObserver.observe(container);

  // Also handle window resize/zoom
  const handleWindowResize = () => {
    updateOverlayPosition();
  };
  window.addEventListener('resize', handleWindowResize);

  // Store cleanup functions
  (overlay as any)._resizeObserver = resizeObserver;
  (overlay as any)._windowResizeHandler = handleWindowResize;

  return overlay;
};

/**
 * Remove image highlight
 */
export const removeImageHighlight = (
  overlay: HTMLElement,
  highlightId: string,
  onUpdate: (highlight: any, action: string) => void
) => {
  console.log('🗑️ removeImageHighlight called', {
    highlightId,
    isTemporary: overlay.dataset.temporary,
    overlayExists: !!overlay,
    parentExists: !!overlay.parentElement,
  });

  try {
    // If this is a full highlight, remove the marker from the image
    if (overlay.dataset.highlightType === 'full') {
      const image = overlay.parentElement?.querySelector('img');
      if (image) {
        delete (image as HTMLImageElement).dataset.hasFullHighlight;
      }
    }

    // Clean up observers
    if ((overlay as any)._resizeObserver) {
      (overlay as any)._resizeObserver.disconnect();
    }
    if ((overlay as any)._windowResizeHandler) {
      window.removeEventListener('resize', (overlay as any)._windowResizeHandler);
    }

    // Clean up notes
    const note = overlay.querySelector('.minimized-note-display');
    if (note) safeRemoveElement(note);

    // Remove note editing tooltips
    document.querySelectorAll('.note-editing-tooltip').forEach((t) => {
      try {
        if ((t as any)._cleanup) {
          (t as any)._cleanup();
        }
        safeRemoveElement(t);
      } catch (error) {
        console.warn('Error removing note tooltip:', error);
      }
    });

    // Trigger mouse leave to close any open tooltips
    const buttons = overlay.querySelectorAll('button');
    buttons.forEach((btn) => {
      try {
        btn.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
      } catch (error) {
        console.warn('Error dispatching mouse leave:', error);
      }
    });

    // Remove overlay from DOM
    console.log('🗑️ Removing overlay from DOM');
    safeRemoveElement(overlay);
    console.log('🗑️ Overlay removed from DOM:', !document.body.contains(overlay));
  } catch (error) {
    console.warn('Error removing image highlight:', error);
  }

  // Only remove from store if not temporary
  if (overlay.dataset.temporary !== 'true') {
    console.log('✅ Removing from store (not temporary)');
    onUpdate({ id: highlightId }, 'remove');
  } else {
    console.log('⏭️ Skipping store removal (temporary)');
  }
};

/**
 * Update image highlight note UI
 */
export const updateImageNoteUI = (
  overlay: HTMLElement,
  highlight: any,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean
) => {
  try {
    // Check if note editor is currently open for this highlight
    const isEditorOpen = document.querySelector(
      `.note-editing-tooltip[data-highlight-id="${highlight.id}"]`
    );

    // Update minimized note only if editor is not open
    const existingNote = overlay.querySelector('.minimized-note-display');
    if (existingNote) safeRemoveMinimizedNote(existingNote);

    if (highlight.note?.trim() && commentsVisible && !isEditorOpen) {
      createMinimizedNote(overlay, highlight.note, () => {
        openImageNoteEditor(overlay, highlight, onUpdate, translate);
      });
    }

    // ALWAYS recreate action buttons to have fresh callbacks
    const existingButtonWrapper = overlay.querySelector('.image-highlight-buttons-wrapper');
    if (existingButtonWrapper) {
      // Detach its listeners (incl. the touch-mode document dismiss) first.
      (existingButtonWrapper as any)._cleanup?.();
      safeRemoveElement(existingButtonWrapper);
    }

    // Create fresh buttons with current highlight data
    const hasNote = !!highlight.note?.trim();
    createActionButtons({
      container: overlay,
      onMessage: () => openImageNoteEditor(overlay, highlight, onUpdate, translate),
      onRemove: () => {
        console.log('🗑️ Remove clicked for:', highlight.id);
        removeImageHighlight(overlay, highlight.id, onUpdate);
      },
      commentsVisible,
      hasNote,
    });

    overlay.dataset.hasNote = hasNote ? 'true' : 'false';
  } catch (error) {
    console.warn('Error updating image note UI:', error);
  }
};

/**
 * Open note editor for image highlight
 */
const openImageNoteEditor = (
  overlay: HTMLElement,
  highlight: any,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any
) => {
  // Close any existing note editors first
  document.querySelectorAll('.note-editing-tooltip').forEach((tooltip) => {
    try {
      // Show any hidden minimized notes before removing the editor
      const parentOverlay = document.querySelector(
        `[data-highlight-id="${tooltip.getAttribute('data-highlight-id')}"]`
      );
      if (parentOverlay) {
        const minimizedNote = parentOverlay.querySelector('.minimized-note-display') as HTMLElement;
        if (minimizedNote) {
          minimizedNote.style.display = 'block';
        }
      }
      // Clean up event listeners before removing
      if ((tooltip as any)._cleanup) {
        (tooltip as any)._cleanup();
      }
      safeRemoveElement(tooltip);
    } catch (error) {
      console.warn('Error closing existing tooltip:', error);
    }
  });

  const config =
    highlight.highlight_type === 'partial' ? TOOLTIP_CONFIG.PARTIAL : TOOLTIP_CONFIG.FULL;
  const anchorRect = () =>
    highlight.highlight_type === 'partial'
      ? overlay.getBoundingClientRect()
      : (overlay.querySelector('.full-image-corner-pointer') || overlay).getBoundingClientRect();

  const position = calculateTooltipPosition(anchorRect(), config);

  // Hide minimized note
  const note = overlay.querySelector('.minimized-note-display') as HTMLElement;
  if (note) note.style.display = 'none';

  // Track the latest saved note value in closure
  let savedNoteValue = highlight.note || '';

  createNoteEditor({
    position,
    // R2-G56: reanchor from the live overlay rect on window resize.
    getPosition: () => calculateTooltipPosition(anchorRect(), config),
    currentNote: highlight.note || '',
    highlightId: highlight.id,
    onSave: (noteValue) => {
      // Just save the note value - don't remove highlight yet
      // Removal will be handled in onClose if note is empty
      const updated = { ...highlight, note: noteValue };

      // If temporary and adding note, save to store permanently
      if (overlay.dataset.temporary === 'true' && noteValue.trim()) {
        onUpdate(updated);
        overlay.dataset.temporary = 'false';
      } else if (overlay.dataset.temporary !== 'true') {
        // Update existing permanent highlight
        onUpdate(updated);
      }

      // Track the saved value for use in onClose
      savedNoteValue = noteValue;

      updateImageNoteUI(overlay, updated, onUpdate, translate, true);
    },
    onDelete: () => {
      try {
        // For image highlights: no note = no highlight
        // Remove the entire highlight when note is deleted
        console.log('🗑️ Image note deleted, removing entire highlight:', highlight.id);

        // Close the note editor tooltip first
        const tooltip = document.querySelector(
          `.note-editing-tooltip[data-highlight-id="${highlight.id}"]`
        );
        if (tooltip) {
          if ((tooltip as any)._cleanup) {
            (tooltip as any)._cleanup();
          }
          safeRemoveElement(tooltip);
        }

        // Remove the highlight
        removeImageHighlight(overlay, highlight.id, onUpdate);
      } catch (error) {
        console.warn('Error deleting image note:', error);
      }
    },
    onClose: () => {
      try {
        // If temporary and no note was added, remove highlight
        if (overlay.dataset.temporary === 'true' && !savedNoteValue.trim()) {
          console.log('🗑️ New image highlight closed without note, removing:', highlight.id);
          removeImageHighlight(overlay, highlight.id, onUpdate);
          return;
        }

        // If existing highlight has empty note after editing, remove it
        if (overlay.dataset.temporary !== 'true' && !savedNoteValue.trim()) {
          console.log('🗑️ Image note emptied and editor closed, removing highlight:', highlight.id);
          removeImageHighlight(overlay, highlight.id, onUpdate);
          return;
        }

        // Get current commentsVisible state
        const currentCommentsVisible =
          useRealtimeExperienceStore.getState().commentsVisible ?? true;

        // Recreate minimized note with latest content
        if (savedNoteValue.trim() && currentCommentsVisible) {
          // Remove old minimized note (it might have stale content)
          const existingNote = overlay.querySelector('.minimized-note-display');
          if (existingNote) safeRemoveMinimizedNote(existingNote);

          // Create fresh minimized note with current content and event handlers
          const updatedHighlight = { ...highlight, note: savedNoteValue };
          createMinimizedNote(overlay, savedNoteValue, () => {
            openImageNoteEditor(overlay, updatedHighlight, onUpdate, translate);
          });
        }
      } catch (error) {
        console.warn('Error closing image note editor:', error);
      }
    },
  });
};
